'use client';

import { useEffect, useRef } from 'react';
import { GoogleTagManagerConsentSettings } from './GoogleTagManager.types';
import { updateConsent } from './util/update-consent';

const LOCAL_STORAGE_KEY = 'gtm-consent-settings';

/**
 * Hook to observe updates to Google Tag Manager's `dataLayer` consent settings
 * and persist them to localStorage.
 */
export const useDataLayerConsentObserver = (shouldPersist = true) => {
  const hasInitialized = useRef(false);

  useEffect(() => {
    let restoreDataLayerPush = null;

    // Initialize consent settings from localStorage (if available)
    const initializeConsentSettings = () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;

      const storedConsent = localStorage.getItem(LOCAL_STORAGE_KEY);

      if (storedConsent) {
        // Update GTM with stored consent settings
        const consentSettings = JSON.parse(
          storedConsent
        ) as GoogleTagManagerConsentSettings;

        updateConsent(consentSettings);
      }
    };

    // Listen to `dataLayerChange` events and save updated consent settings
    const handleDataLayerChange = (event: CustomEvent) => {
      const rawArgs = event.detail;

      // Filter only for GTM consent updates (['consent', 'update'])
      if (rawArgs?.[0]?.[0] === 'consent' && rawArgs?.[0]?.[1] === 'update') {
        const consentSettings =
          rawArgs?.[0]?.[2] as GoogleTagManagerConsentSettings;

        // Persist consent settings to localStorage
        localStorage.setItem(
          LOCAL_STORAGE_KEY,
          JSON.stringify(consentSettings)
        );
      }
    };

    if (shouldPersist) {
      restoreDataLayerPush = createDataLayerPushEventDispatcher();

      window.addEventListener(
        'dataLayerChange',
        handleDataLayerChange as EventListener
      );
    }

    initializeConsentSettings();

    return () => {
      if (shouldPersist) {
        restoreDataLayerPush?.();

        window.removeEventListener(
          'dataLayerChange',
          handleDataLayerChange as EventListener
        );
      }
    };
  }, [shouldPersist]);
};

/**
 * Creates a custom event dispatcher for the `dataLayer` push method.
 * This allows tracking changes to the `dataLayer` and dispatching
 * a custom event when changes occur.
 */
const createDataLayerPushEventDispatcher = () => {
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  const originalPush = window.dataLayer.push;

  window.dataLayer.push = function (...args: any[]) {
    const result = originalPush.apply(this, args);

    const event = new CustomEvent('dataLayerChange', {
      detail: args,
    });
    window.dispatchEvent(event);

    return result;
  };

  return () => {
    window.dataLayer.push = originalPush;
  };
};
